/**
 * Display-time PII redaction helpers for the Experience data visualizer.
 *
 * Rule: visualizer shows half-redacted values ("li***@example.com", "L*** W."),
 * CSV download keeps full unredacted values. The underlying JSON response is
 * always the full data; redaction happens at render time in the visualizer.
 */

/**
 * Email — show first 2 chars of local part + *** + full domain.
 *   "lisa.wilson@example.com" → "li***@example.com"
 *   "a@b.co"                  → "a***@b.co"
 *   null/empty                → "—"
 */
export function redactEmail(email: string | null | undefined): string {
    if (!email) return "—"
    const [local, domain] = email.split("@")
    if (!local || !domain) return "***"
    const visible = local.slice(0, Math.min(2, local.length))
    return `${visible}***@${domain}`
}

/**
 * Name — first initial + *** of first name, plus first initial + . of last name.
 *   ("Lisa", "Wilson")  → "L*** W."
 *   ("Lisa", null)       → "L***"
 *   (null, "Wilson")     → "W."
 *   (null, null)         → "—"
 */
export function redactName(first: string | null | undefined, last: string | null | undefined): string {
    const f = first?.trim() || ""
    const l = last?.trim() || ""
    if (!f && !l) return "—"
    const fPart = f ? f[0] + (f.length > 1 ? "***" : "") : ""
    const lPart = l ? " " + l[0] + "." : ""
    return (fPart + lPart).trim()
}

/**
 * External ID — show first 6 chars + ellipsis (rest hidden).
 *   "aabbccddeeff" → "aabbcc…"
 *   length ≤ 6     → returned unchanged
 *   null/empty     → "—"
 */
export function redactExternalId(ext: string | null | undefined): string {
    if (!ext) return "—"
    return ext.length > 6 ? ext.slice(0, 6) + "…" : ext
}

/**
 * Wallet ID — show first 8 chars + ellipsis. UUIDs become "0a1dece0…".
 */
export function redactWalletId(w: string | null | undefined): string {
    if (!w) return "—"
    return w.length > 8 ? w.slice(0, 8) + "…" : w
}

/**
 * One-liner: pick the best identifier for display, redacted form.
 * Priority: email > externalId > walletId. Used in the visualizer header.
 */
export function bestRedactedIdentifier(opts: {
    email?: string | null
    externalId?: string | null
    walletId?: string | null
}): string {
    if (opts.email) return redactEmail(opts.email)
    if (opts.externalId) return redactExternalId(opts.externalId)
    if (opts.walletId) return redactWalletId(opts.walletId)
    return "—"
}
